import { bool } from "prop-types";
import {
  getApiUrl,
  getLocalUser,
  getLocalUserId,
} from "../components/functions/utilities";
import {
  AccessUsers,
  ActiveAlerts,
  Channel,
  Chart,
  DataAlert,
  userWsLink,
  Message,
  Metrics,
  Notification,
  PivotTable,
  Report,
  ReportStatus,
  ReportVersion,
  ReportVersionWithName,
  Tracker,
  TrackerSummary,
  TrackerWithRecipientNames,
  UnreadMessageCount,
  User,
  UserFile,
  UserFileSheetName,
  UserGroup,
  AccessTokens,
  IpWorkspaceReportInfo,
  NewMetrics,
  NewActiveAlerts,
  IIntegrationPartner,
  Cards,
  JobType,
  WorkspaceStatus,
  DashboardDataPieces,
  PinnedVisualIdentifiers,
  PinnedVisualType,
  Cohort,
  DefaultCohortFilters,
  Company,
  InsightVote,
  GoalsAndIdeas,
  InsightTypes,
  GoalsHubResponse,
  LoadingProgressType,
  PcColdStates,
  ProductFetchingProgress,
  TaxInputInf,
  LinkType,
  DisbursementColdState,
  PinnedVisual,
  ApplicationContextType,
  UserRole,
} from "../interfaces/interfaces";
import { updateChannelPayload } from "../pages/Dashboard/Components/ChatBox/chatHeader/chatHeader";
import { TrackerPayload } from "../pages/Dashboard/Components/trackerModal/trackerModal";
import {
  DecisionWithLinking,
  GetReportDataParams,
  ReportDataFromDH,
  TopReportsWithDatapiece,
} from "../pages/DecisionHub/interfaces";
import { AddChannelsPayload, UnreadCount } from "../redux/chat/chatSlice";
import { AddSummaryPayload } from "../redux/dataAlerts/dataAlertsSlice";
import API from "./api";
import { reject } from "lodash";
import { resolve } from "path";

export const JIGSAW_DEV_REST_LOCATION = `${
  import.meta.env.VITE_JIGSAW_DEV_REST_LOCATION
}`;

class RestService {
  parseExcelFileChartsAndTables = (
    reportId: number
  ): Promise<{
    chart: Chart[];
    pivot: PivotTable[];
    chartError: [];
    pivotError: [];
  }> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/${getApiUrl("parseExcelFileChartsAndTables")}`, {
        reportId,
      })
        .then((res) => {
          if (res.data.success) {
            resolve({
              chart: res.data.data.charts,
              pivot: res.data.data.tabels,
              chartError: res.data.chartError,
              pivotError: res.data.tableError,
            });
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  parseExcelFileSheetNames = (
    reportId: number
  ): Promise<UserFileSheetName[]> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/${getApiUrl("parseExcelFileSheetNames")}`, {
        reportId,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.userFile.sheetNames);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  getListOfSheets = (reportId: number): Promise<UserFile> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/getListOfSheets`, { reportId })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.userfile);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  updateUserTempleteSheets = (
    templateExcelFileId: number,
    templateExcelFileSheetNames: UserFileSheetName[]
  ): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/updateUserFiles`, {
        templateExcelFileId,
        templateExcelFileSheetNames,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.success);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  updateReportEmailBody = (
    reportId: Number,
    emailSubject: String,
    emailBody: string
  ): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/updateRepoertEmail`, {
        reportId,
        emailSubject,
        emailBody,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.success);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  updateSingleReportDump = (
    multiple: number,
    reportId: number,
    dumpUserFileId: number
  ): Promise<{
    report: Report;
    chartError: any;
    pivotError: any;
  }> => {
    let userId = getLocalUserId();
    return new Promise((resolve, reject) => {
      API.post(`/Reports/${getApiUrl("updateReport")}`, {
        multiple,
        reportId,
        dumpUserFileId,
        userId,
      })
        .then((res) => {
          if (res.data.success) {
            resolve({
              report: res.data.report,
              chartError: res.data.errorList.chartError,
              pivotError: res.data.errorList.tableError,
            });
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  updatemultipleDump = (
    multiple: number,
    reportId: number,
    templatePath: object
  ): Promise<{
    report: Report;
    chartError: any;
    pivotError: any;
  }> => {
    let userId = getLocalUserId();
    return new Promise((resolve, reject) => {
      API.post(`/Reports/${getApiUrl("updateReport")}`, {
        multiple,
        reportId,
        templatePath,
        userId,
      })
        .then((res) => {
          if (res.data.success) {
            resolve({
              report: res.data.report,
              chartError: res.data.errorList.chartError,
              pivotError: res.data.errorList.tableError,
            });
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  getPinnedVisualsIdentifier = (
    reportId: number
  ): Promise<PinnedVisualIdentifiers> => {
    return new Promise((resolve, reject) => {
      if (!reportId) reject("Report ID not found");
      API.get(`/pinnedVisuals?reportId=${reportId}`)
        .then((res) => {
          if (res.data.success) resolve(res.data.data);
          else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  getReport = (
    reportId: number,
    reportVersionId: number = 0
  ): Promise<{
    chart: Chart[];
    pivot: PivotTable[];
    report: Report;
    reportVersion: ReportVersion;
    user: any;
    createdUser: User;
  }> => {
    //
    return new Promise((resolve, reject) => {
      API.post(`/Reports/getReportDetails`, {
        reportId,
        reportVersionId,
      })
        .then((res) => {
          if (res.data.success) {
            resolve({
              chart: res.data.data.charts,
              pivot: res.data.data.tables,
              report: res.data.data.report,
              reportVersion: res.data.data.reportVersion,
              user: res.data.data.recipeints,
              createdUser: res.data.data.user,
            });
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  getReportsByUserId = (
    pageNumber: number,
    pageCount: number,
    search_string: string | null,
    showDraft: boolean = false,
    userId = null
  ): Promise<{
    reports: { report: Report; latestVersion: ReportVersion; user: User }[];
    totalReportsCount: number;
    ipWorkspaceInfo: { [x: string]: IpWorkspaceReportInfo };
  }> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/getReportByUserId`, {
        pageNumber,
        pageCount,
        search_string,
        showDraft,
        userId,
      })
        .then((res) => {
          if (res.data.success) {
            resolve({
              reports: res.data.reports,
              totalReportsCount: res.data.totalRecord,
              ipWorkspaceInfo: res.data.ipWorkspaceDetails,
            });
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  getReportsListByUserId = (body: any): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/getReportByUserId`, body)
        .then((res) => {
          if (res.data.success) {
            resolve(res);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  getAllUsersByCompany = (): Promise<User[]> => {
    return new Promise((resolve, reject) => {
      API.post(`/Users/GetUsersByCompanyUserPlatform`, {})
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.users);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  getAllGroupByCompany = (): Promise<UserGroup[]> => {
    return new Promise((resolve, reject) => {
      API.post(`/UserGroups/getUserGroup`, {})
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.userGroups);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  getAllUsers = (): Promise<User[]> => {
    return new Promise((resolve, reject) => {
      API.post(`/Users/GetAll`)
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.users);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  getAllUserGroups = (): Promise<UserGroup[]> => {
    return new Promise((resolve, reject) => {
      API.get(`/UserGroups/getAll`)
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.usergroup);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  createNewReport = (
    reportName: string,
    workspaceId: string,
    ipName: string
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/newReport`, {
        reportName,
        workspaceId,
        ipName,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.report);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  updateReportName = (reportId: number, name: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/updateReportName`, {
        reportId,
        name,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.report);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  updateChartName = (chartId: number, title: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/updateChartName`, {
        chartId,
        title,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.chart);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  updateChannelName = (
    channelId: number,
    channelName: string
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post(`/chat/channel/editname`, {
        channelId,
        channelName,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.channel);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  deleteChat = (channelId: number): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post(`/Chat/deleteChannel`, {
        channelId,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.success);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  updatePivotName = (pivotId: number, title: string): Promise<PivotTable> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/updatePivotName`, {
        pivotId,
        title,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.pivotTable);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  createTemplate = (reportId): Promise<void> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/${getApiUrl("templateCreate")}`, {
        reportId,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.report);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  deleteReport = (reportId: string): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/DeleteReport`, { reportId })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.success);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  toggleAllDashboardSelectionAllUser = (
    reportId: number,
    value: boolean
  ): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/toggleAllDashboardSelectionAllUser`, {
        reportId,
        value,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.success);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  toggleChartSelection = (
    chartId: number,
    showOn: string,
    value: boolean
  ): Promise<Chart> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/toggleChartSelection`, {
        chartId,
        showOn,
        value,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.chart);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  toggleChartAccess = (
    chartId: number,
    value: boolean,
    userId: string
  ): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/toogleChartAccess`, {
        chartId,
        value,
        userId,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.success);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  togglePivotAccess = (
    pivotId: number,
    value: boolean,
    userId: string
  ): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/tooglePivotAccess`, {
        pivotId,
        value,
        userId,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.success);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  toggleChartDashboardAllUser = (
    chartId: number,
    value: boolean,
    reportId: string
  ): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/toggleChartDashboardAllUser`, {
        chartId,
        reportId,
        value,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.success);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  togglePivotSelection = (
    pivotId: number,
    showOn: string,
    value: boolean
  ): Promise<PivotTable> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/togglePivotSelection`, {
        pivotId,
        showOn,
        value,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.pivotTable);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  togglePivotDashboardAllUser = (
    pivotId: number,
    value: boolean,
    reportId: string
  ): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/togglePivotDashboardAllUser`, {
        pivotId,
        reportId,
        value,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.success);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  /*Not in use*/
  updateReport = (report: Object): Promise<void> => {
    return new Promise((resolve, reject) => {
      API.post(`/reports/updateReport`, { report })
        .then((res) => {
          if (res.data.success) {
            resolve();
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  reportStatus = (reportId: string, status: ReportStatus): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/reportStatus`, { reportId, status })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.success);
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  setReportDataPoints = (
    reportId: number,
    dataSets: Object[]
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      API.post(`/reports/setDataPoints`, {
        reportId,
        dataSets,
      })
        .then((res) => {
          if (res.data.success) resolve();
          else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  setReportRecipients = (
    reportId: number,
    users: Object[],
    userGroups: Object[]
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      API.post(`/reports/setRecipients`, {
        reportId,
        users,
        userGroups,
      })
        .then((res) => {
          if (res.data.success) resolve();
          else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  updateRecipientList = (reportId, recipeint): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/updateRecipientList`, {
        reportId,
        recipeint,
      })
        .then((res) => {
          if (res.data.success) resolve(res.data.success);
          else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  removerRecipientFromReport = (recipientId, reportId): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/removerRecipientFromReport`, {
        recipientId,
        reportId,
      })
        .then((res) => {
          if (res.data.success) resolve(res.data.success);
          else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  sendReportEmail = (reportId): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/sendReportEmail`, {
        reportId,
      })
        .then((res) => {
          if (res.data.status) resolve(res.data);
          else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  downloadReportFile = (reportId): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/downloadReportFile`, {
        reportId,
      })
        .then((res) => {
          if (res.data.status) resolve(res.data);
          else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  emailPreivew = (
    reportId
  ): Promise<{ report: Report; userDetails: User; charts: Chart[] }> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/emailpreview`, {
        reportId,
      })
        .then((res) => {
          if (res.data.success) resolve(res.data.data);
          else reject(res.data.error);
        })
        .catch(reject);
    });
  };
  updateMembers = (
    channelId: number,
    userId: number,
    isAdd: boolean,
    loginId: number
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post(`/Chat/AddMembers`, {
        channelId,
        userId,
        isAdd,
        loginId,
      })
        .then((res) => {
          if (res.data.success) resolve(res.data.data);
          else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  GetReportDetails = (reportId: number): Promise<ReportVersionWithName> => {
    //
    return new Promise((resolve, reject) => {
      API.get("/reports/reportname", {
        params: {
          reportId: reportId,
        },
      })
        .then((res) => {
          if (res.data.success)
            resolve(res.data.report as ReportVersionWithName);
          else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  GetAccessUsersForDataPiece = (
    reportVersion: number,
    noAccessUsers: number[]
  ): Promise<AccessUsers[]> => {
    return new Promise((resolve, reject) => {
      API.get(`/Chat/getAccessUsers`, {
        params: {
          reportVersion,
          noAccessUsers: noAccessUsers.toString(),
        },
      })
        .then((res) => {
          if (res.data.success) resolve(res.data.users);
          else reject(res.data.error);
        })
        .catch(reject);
    });
  };
  getUpdateVersions = (parentVersionId: number): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/getUpdateVersions/`, { parentVersionId })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  getUpdateChartRange = (
    reportVersionsIds: number[],
    chartIdentifier: string
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/getUpdateChartRange/`, {
        reportVersionsIds,
        chartIdentifier,
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  getSingleChart = (
    reportVersionId: number,
    chartIdentifier: string
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      //
      API.post(`/Reports/getSingleChart/`, { reportVersionId, chartIdentifier })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  getAllVersions = (reportId: number): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/getAllVersions`, { reportId })
        .then((res) => {
          resolve(res.data);
        })
        .catch(reject);
    });
  };

  getVersionDetail = (reportVersionId): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/getVersionDetail`, { reportVersionId })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  checkReportAccess = (reportId: number): Promise<void> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/checkReportAccess`, {
        reportId,
      })
        .then((res) => {
          if (res.data.success) resolve();
          else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  getChannelByChartIdAndUserId = (
    chartId: number,
    reportVersion: number,
    priorityChannelId: number,
    channelType: number
  ): Promise<{ channels: Channel[]; unreadMessages: UnreadMessageCount[] }> => {
    return new Promise((resolve, reject) => {
      API.get("/Chat/getChannels", {
        params: { chartId, reportVersion, priorityChannelId, channelType },
      })
        .then((res) => {
          if (res.data.success)
            resolve({
              channels: res.data.channels,
              unreadMessages: res.data.unreadCount,
            });
          else reject(res.data.error);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  getChannelMessages = (channelId: number): Promise<Message[]> => {
    return new Promise((resolve, reject) => {
      API.get("/chat/channel/messages", { params: { channelId } })
        .then((res) => {
          if (res.data.success) resolve(res.data.messages);
          else reject(res.data.error);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  createChannel = (channel: Channel): Promise<Channel> => {
    return new Promise((resolve, reject) => {
      API.post("/Chat/createChannel", channel).then((res) => {
        if (res.data.success) resolve(res.data.channel);
        else reject(res.data.error);
      });
    });
  };

  toggleChatAccess = (
    channelId: number,
    userId: string,
    value: boolean
  ): Promise<Channel> => {
    return new Promise((resolve, reject) => {
      API.post("/Chat/toggleChannelAccess", { channelId, userId, value }).then(
        (res) => {
          if (res.data.success) resolve(res.data.channel);
          else reject(res.data.error);
        }
      );
    });
  };

  getChannelUnreadCount = (
    channelId: string
  ): Promise<UnreadMessageCount[]> => {
    return new Promise((resolve, reject) => {
      API.get("/chat/channel/unreadcount", { params: { channelId } })
        .then((res) => {
          //
          if (res.data.success) resolve(res.data.unreadCount);
          else reject(res.data.error);
        })
        .catch((error) => {
          //
          reject(error);
        });
    });
  };

  updateChannelBulkMessageStatus = (
    channelId: number
    // abortController: AbortController
  ): Promise<UnreadMessageCount[]> => {
    return new Promise((resolve, reject) => {
      API.put(
        "/chat/channel/messagestatus",
        { channelId }
        // { signal: abortController.signal }
      )
        .then((res) => {
          //
          if (res.data.success) resolve(res.data.unreadCount);
          else reject(res.data.error);
        })
        .catch((error) => {
          //
          reject(error);
        });
    });
  };

  getAllPivotsFromVersion = (
    reportVersionId: number,
    pivotIdentifier: string
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post("/Reports/getAllPivotsFromVersion", {
        reportVersionId,
        pivotIdentifier,
      })
        .then((res) => {
          resolve(res.data);
        })
        .catch(reject);
    });
  };

  insertSingleChart = (chart: {}, reportId): Promise<any> => {
    return new Promise((resolve, reject) => {
      //
      API.post("/Reports/insertChart", {
        chart,
        reportId,
      })
        .then((res) => {
          resolve(res.data);
        })
        .catch(reject);
    });
  };

  setFirebaseToken = (token): Promise<void> => {
    return new Promise((resolve, reject) => {
      API.post(`/Notification/setFirebaseToken`, { token })
        .then((res) => {
          resolve();
        })
        .catch(reject);
    });
  };

  getAppNotificationList = (pageNo, pageCount): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post(`/Notification/getAppNotificationList`, { pageNo, pageCount })
        .then((res) => {
          resolve(res.data);
        })
        .catch(reject);
    });
  };

  getAllTrendChart = (
    reportVersionId: number,
    chartIdentifier: string
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post("/Reports/getAllTrendChart", {
        reportVersionId,
        chartIdentifier,
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  getAllTrendPivot = (
    reportVersionId: number,
    pivotIdentifier: string
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post("/Reports/getAllPivotTrendData", {
        reportVersionId,
        pivotIdentifier,
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  deleteChartById = (id: number): Promise<void> => {
    return new Promise((resolve, reject) => {
      API.post("/Reports/deleteChartById", {
        id,
      })
        .then(() => resolve())
        .catch(reject);
    });
  };

  createTracker = (payload: TrackerPayload): Promise<Tracker> => {
    return new Promise((resolve, reject) => {
      API.post("/tracker", payload)
        .then((res) => {
          if (res.data.success) resolve(res.data.tracker);
          else reject("Error in creating tracker");
        })
        .catch(reject);
    });
  };

  getChannelsByLatestActivity = (): Promise<{
    pinnedChannels: Channel[];
    unpinnedChannels: Channel[];
    unreadCount: UnreadCount[];
  }> => {
    return new Promise((resolve, reject) => {
      API.get("/chat/channels/latestactivity")
        .then((res) => {
          if (res.data.success) {
            //
            resolve({
              unpinnedChannels: res.data.unpinnedChannels,
              pinnedChannels: res.data.pinnedChannels,
              unreadCount: res.data.unreadCount,
            });
          } else reject("Error in getting channels");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  getTrackersInOrder = (): Promise<TrackerWithRecipientNames[]> => {
    return new Promise((resolve, reject) => {
      API.get("/tracker")
        .then((res) => {
          if (res.data.success) {
            //
            resolve(res.data.trackers);
          } else reject("Error in getting trackers");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  togglePinChannel = (channelId: number, value: boolean): Promise<Channel> => {
    return new Promise((resolve, reject) => {
      API.post("/chat/togglepinchannel", { channelId, value })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.channel);
          } else reject("Error in updating pin status");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  getTrackersByMonth = (
    monthIndex: number,
    trackerType
  ): Promise<Tracker[]> => {
    return new Promise((resolve, reject) => {
      API.get("/tracker/bymonth", {
        params: {
          monthIndex,
          trackerType,
        },
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.trackers);
          } else reject("Error in fetching trackers");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  getTrackersRecipientsDetails = (
    referenceId: number,
    trackerType: number
  ): Promise<AccessUsers[]> => {
    return new Promise((resolve, reject) => {
      API.get("/tracker/accessusers", {
        params: {
          referenceId,
          trackerType,
        },
      })
        .then((res) => {
          if (res.data.success) {
            return resolve(res.data.users);
          } else reject("Error in fetching users");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  updateTracker = (payload: TrackerPayload): Promise<Tracker> => {
    return new Promise((resolve, reject) => {
      API.put("/tracker", payload)
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.tracker);
          } else reject("Error in updating trackers");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  toggleTrackerStatus = (
    trackerId: number,
    status: number
  ): Promise<TrackerWithRecipientNames> => {
    return new Promise((resolve, reject) => {
      API.post("/tracker/togglestatus", { trackerId, status })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.tracker);
          } else reject("Failed to upade the status");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  getDatapiece = (
    referenceId: number,
    channelType: number
  ): Promise<Chart | PivotTable> => {
    return new Promise((resolve, reject) => {
      API.get("/chat/datapiece", {
        params: {
          referenceId,
          channelType,
        },
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.datapiece);
          } else reject("Failed to upade the status");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  deleteTracker = (trackerId: number): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.delete("/tracker", {
        params: {
          trackerId,
        },
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.status);
          } else reject("Failed to delete the tracker");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  deleteChannel = (channelId: number): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.delete("/chat/channel", {
        params: {
          channelId,
        },
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.status);
          } else reject("Failed to delete the channel");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  getTrackerByDate = (
    date: string,
    trackerType: string
  ): Promise<TrackerWithRecipientNames[]> => {
    return new Promise((resolve, reject) => {
      API.get("/tracker/date", {
        params: {
          date,
          trackerType,
        },
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.trackers);
          } else reject("Failed to get the tracker");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  getNotifications = (
    category: string,
    skip: number,
    limit: number
  ): Promise<{ unreadCount: number; notifications: Notification[] }> => {
    return new Promise((resolve, reject) => {
      API.get("/notification", {
        params: {
          category,
          skip,
          limit,
        },
      })
        .then((res) => {
          //
          if (res.data.success) {
            resolve({
              unreadCount: res.data.unreadCount,
              notifications: res.data.notifications,
            });
          } else reject("failed to get notifications");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  updateNotificationStatusById = (
    id: number,
    status: number
  ): Promise<Notification> => {
    return new Promise((resolve, reject) => {
      API.post("/notification/status", {
        id,
        status,
      })
        .then((res) => {
          //
          if (res.data.success) {
            resolve(res.data.notification);
          } else reject("failed to update notifications");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  updateNotificationStatusByUser = (
    status: number
  ): Promise<{ message: string }> => {
    return new Promise((resolve, reject) => {
      API.post("/notification/changeNotificationStatusByUser", {
        status,
      })
        .then((res) => {
          //
          if (res.data.status) {
            resolve({ message: "Successfully updated notifications" });
          } else reject("failed to update notifications");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  clearAllNotificationByUserAndType = (
    notificationType: number
  ): Promise<{ message: string }> => {
    return new Promise((resolve, reject) => {
      API.post("/notification/clear", {
        notificationType,
      })
        .then((res) => {
          //
          if (res.data.status) {
            resolve({ message: "Successfully cleared notifications" });
          } else reject("failed to update notifications");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  getAllMetrics = (
    reportId: number,
    chartIdentifier: string
  ): Promise<NewMetrics[]> => {
    return new Promise((resolve, reject) => {
      API.get("/dataalerts/allmetrics", {
        params: {
          reportId,
          chartIdentifier,
        },
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.metrics);
          } else reject("Failed to fetch Metrics");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  createNewAlert = (alertPayload: DataAlert): Promise<DataAlert> => {
    return new Promise((resolve, reject) => {
      API.post("/dataalerts", alertPayload)
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.dataAlert);
          } else reject("Failed to create new alert");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  getDataAlertSummary = (): Promise<AddSummaryPayload> => {
    return new Promise((resolve, reject) => {
      API.get("/dataalerts/summary")
        .then((res) => {
          if (res.data.success) {
            resolve({
              activeAlertCount: res.data.activeAlertCount,
              triggeredAlertCount: res.data.triggeredAlertCount,
              lastMonthAlerts: res.data.lastMonthAlerts,
              mostTriggeredAlerts: res.data.mostTriggeredAlerts,
              mostWorkspaceAlerts: res.data.mostWorkspaceAlerts,
            } as AddSummaryPayload);
          } else reject("Failed to get the data alert");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  getActiveAlerts = (reportId: number): Promise<DataAlert[]> => {
    return new Promise((resolve, reject) => {
      API.get("/dataalerts/active", {
        params: {
          reportId,
        },
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.activeAlerts);
          } else reject("Failed to get active alerts");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  getTriggeredAlertsHistory = (alertId: number): Promise<NewActiveAlerts[]> => {
    return new Promise((resolve, reject) => {
      API.get("/dataalerts/triggeredalerts/history", {
        params: {
          alertId,
        },
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.alertHistory);
          } else reject("Failed to get alert history");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  getTriggeredAlerts = (reportId: number): Promise<NewActiveAlerts[]> => {
    return new Promise((resolve, reject) => {
      API.get("/dataalerts/triggeredalerts", {
        params: {
          reportId,
        },
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.triggeredAlerts);
          } else reject("Failed to get triggered alerts");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  updateActiveAlerts = (updatePayload: DataAlert): Promise<DataAlert> => {
    return new Promise((resolve, reject) => {
      API.put("/dataalerts/activealert", {
        alert: updatePayload,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.activeAlert);
          } else reject("Failed to update alert");
        })
        .catch(reject);
    });
  };

  getTrackerSummary = (): Promise<TrackerSummary> => {
    return new Promise((resolve, reject) => {
      API.get("/tracker/summary")
        .then((res) => {
          if (res.data.success) {
            resolve({
              activeTrackersCount: res.data.activeTrackersCount,
              overdueTrackersCount: res.data.overdueTrackersCount,
              closedTrackersCount: res.data.closedTrackersCount,
              lastMonthTrackers: res.data.lastMonthTrackers,
              topOverdueTrackers: res.data.topOverdueTrackers,
              topUpcomingTrackers: res.data.topUpcomingTrackers,
            });
          } else reject("Failed to get tracker summary");
        })
        .catch(reject);
    });
  };

  getTrackersByType = (type: string): Promise<TrackerWithRecipientNames[]> => {
    return new Promise((resolve, reject) => {
      API.get(`/tracker/${type}`)
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.trackers);
          } else reject("Failed to get trackers");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  deleteActiveAlert = (alertId: number): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.delete("/dataalerts/activealert", {
        params: {
          alertId,
        },
      })
        .then((res) => {
          if (res.data.success) {
            resolve(true);
          } else reject("Failed to delete active alert");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  deleteTriggeredAlert = (alertId: number): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.delete("/dataalerts/triggeredalert", {
        params: {
          alertId,
        },
      })
        .then((res) => {
          if (res.data.success) {
            resolve(true);
          } else reject("Failed to delete triggered alert");
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  getReportChartsByCategory = (
    reportId: number,
    reportVersion?: number,
    category?: string
  ): Promise<{
    charts: Chart[];
    pivotTable: PivotTable[];
    cards: Cards[];
    cohorts: Cohort[];
  }> => {
    return new Promise((resolve, reject) => {
      console.log("calling datapiece api");
      API.get(`/reports/${reportId}/datapiece`, {
        params: {
          reportVersion,
          category,
        },
      })
        .then((res) => {
          if (res.data.success) {
            // res.data.cards = res.data?.cards?.map((item) => {
            //   item.config["formula"] =
            //     "New Customers who became Repeat Customers / Total Number of New Customers";
            //   return item;
            // });
            // res.data.cohorts =
            //   res.data?.cohorts?.map((item) => {
            //     if (item.title === "Net Sales Cohort")
            //       item.config["topHeader"] = [
            //         { col: item.config.colSpan, name: "" },
            //         { col: 13, name: "Repeat Orders" },
            //       ];
            //     return item;
            //   }) ?? [];
            resolve({
              charts: res.data.charts,
              pivotTable: res.data.pivotTable,
              cards: res.data.cards,
              cohorts: res.data.cohorts,
            });
          } else {
            resolve({
              charts: [],
              pivotTable: [],
              cards: [],
              cohorts: [],
            });
          }
        })
        .catch((error) => {
          //
          console.log("error", error);
          return reject(error.response.data);
        });
    });
  };

  addCharts = (charts: [], reportVersionId: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      //
      const body = {
        charts,
        reportVersionId,
      };
      API.post("/Charts/addChartsAndTables", body)
        .then((res) => {
          //
          resolve(res.data);
        })
        .catch((err) => {
          //
        });
    });
  };

  getIntegrationWorkspaces = ({
    reportId = null,
    userId = null,
    companyId = null,
  }: {
    reportId?: number;
    userId?: number;
    companyId?: number;
  }): Promise<userWsLink> => {
    return new Promise((resolve, reject) => {
      console.log("getIntegrationWorkspaces called", companyId);
      const user = getLocalUser();
      // console.log("user", user);
      // ?id=${
      //     userId ? userId : user.id
      //   }&reportId=${reportId}&companyId=${companyId}
      API.get(`/IntegrationWorkspace/getIntegrationWorkspace`, {
        params: {
          id: userId ? userId : user.id,
          reportId: reportId,
          companyId: companyId,
        },
      })
        .then((res: any) => {
          if (res.data.success) resolve(res.data.integrationWorkspace);
          else resolve({ userId: 0, authenticatedWs: [] });
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  addIntegrationWorkspace = (
    integrationWorkspace: userWsLink
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      API.post("/IntegrationWorkspace/addIntegrationWorkspace", {
        integrationWorkspace,
      })
        .then((res) => {
          //
        })
        .then((res) => {})
        .catch((err) => {
          reject(err);
        });
    });
  };

  updateIntegrationWorkspace = (
    integrationWorkspace: userWsLink
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      API.post("/IntegrationWorkspace/updateIntegrationWorkspace", {
        integrationWorkspace,
      })
        .then((res) => {
          //
        })
        .then((res) => {})
        .catch((err) => {
          reject(err);
        });
    });
  };

  getUserAccessTokensByUserId = (agCompanyId: number = null): Promise<any> => {
    return new Promise((resolve, reject) => {
      const user = getLocalUser();
      const companyId = user?.role == "Agency" ? agCompanyId : user.companyId;
      API.get(
        `/AccessTokens/getUserAccessTokensByUserId?companyId=${companyId}`
      )
        .then((res: any) => {
          if (res.data.success) resolve(res.data?.accessTokens);
          else resolve([]);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  getAccessToken = (integrationPartnerId: string): Promise<AccessTokens> => {
    return new Promise((resolve, reject) => {
      const user = getLocalUser();
      API.get(
        `/AccessTokens/getAccessTokens?userId=${user.id}&integrationPartnerId=${integrationPartnerId}`
      )
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  refreshNow = ({
    ip,
    jobType,
    agencyClientId = null,
  }: {
    ip: IIntegrationPartner;
    jobType: JobType;
    agencyClientId?: number;
  }): Promise<any> => {
    return new Promise((resolve, reject) => {
      const userDetail = getLocalUser();
      const body = {
        companyId: userDetail.companyId,
        userId: userDetail.id,
        integrationPartner: ip,
        jobType,
        extraDetails: {
          isAgency: userDetail.role === UserRole.Agency,
          agencyClientId: agencyClientId,
        },
      };
      API.post(`/Charts/getLambdaCharts`, body)
        .then((res: any) => {
          resolve(res.data);
        })
        .catch((err) => {
          console.log("err");
          reject(err);
        });
    });
  };

  lambdaCharts = ({
    workspace,
    integrationPartner,
    companyId,
    dataPeriod,
    jobType,
    userId,
    agencyClientId = null,
  }: {
    workspace: any;
    integrationPartner: any;
    companyId: number;
    dataPeriod: any;
    jobType: JobType;
    userId: number;
    agencyClientId?: number;
  }): Promise<any> => {
    return new Promise((resolve, reject) => {
      const userDetail = getLocalUser();
      const body = {
        workspace,
        integrationPartner,
        companyId,
        dataPeriod,
        jobType,
        userId,
        extraDetails: {
          isAgency: userDetail.role === UserRole.Agency,
          agencyClientId: agencyClientId,
        },
      };
      // const body = {
      //   userId: userDetail.id,
      //   user: ipId,
      //   workspaceId: wsId,
      //   isNewWorkspace: true,
      //   wsName,
      //   ipName,
      //   refreshInterval,
      //   dataPeriod,
      //   integrationPartner: integrationPartner,
      //   companyId,
      // };
      // API.post(`https://9tr2rs0ttj.execute-api.ap-south-1.amazonaws.com/eda/`, body)
      API.post(`/Charts/getLambdaCharts`, body)
        .then((res: any) => {
          resolve(res.data);
        })
        .catch((err) => {
          console.log("err");
          reject(err);
        });
    });
  };

  // consolidatedLambdaCharts = (
  //   wsId: string,
  //   ipId: string,
  //   wsName: string,
  //   ipName: string,
  //   refreshInterval: string,
  //   dataPeriod: string,
  //   mandatoryIp: IIntegrationPartner[],
  //   optionalIp: IIntegrationPartner[]
  // ): Promise<any> => {
  //   return new Promise((resolve, reject) => {
  //     const userDetail = getLocalUser();
  //     const body = {
  //       userId: userDetail.id,
  //       user: ipId,
  //       workspaceId: wsId,
  //       isNewWorkspace: true,
  //       wsName,
  //       ipName,
  //       refreshInterval,
  //       dataPeriod,
  //       mandatoryIp,
  //       optionalIp,
  //     };
  //     // API.post(`https://9tr2rs0ttj.execute-api.ap-south-1.amazonaws.com/eda/`, body)
  //     API.post(`/Charts/consolidatedLambdaCall`, body)
  //       .then((res: any) => {
  //         resolve(res.data);
  //       })
  //       .catch((err) => {
  //         console.log("err");
  //         reject(err);
  //       });
  //   });
  // };

  sendSupportDetails = (title, emailBody): Promise<any> => {
    return new Promise((resolve, reject) => {
      const body = {
        title,
        emailBody,
      };
      API.post("/support/sendSupport", body)
        .then((res) => {
          //
        })
        .catch((err) => {
          //
        });
    });
  };

  sendApplicationDetail = (name, link): Promise<any> => {
    return new Promise((resolve, reject) => {
      const body = {
        name,
        link,
      };
      API.post("/IntegrationWorkspace/application", body)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  sendRequestMetrics = (
    name,
    formula,
    description,
    wsName,
    ipName
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      const body = {
        name,
        formula,
        description,
        wsName,
        ipName,
      };
      API.post("/support/requestMetrics", body)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  pinToDashboard = ({
    identifier,
    reportId,
    type,
  }: {
    identifier: string;
    reportId: number;
    type: PinnedVisualType;
  }): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.post("/pinnedVisuals/pinVisuals", [
        {
          identifier,
          reportId,
          type,
        },
      ])
        .then((res) => {
          if (res.data.success) resolve(true);
          else reject("Failed to pin visual to dashboard");
        })
        .catch(reject);
    });
  };

  unpinFromDashboard = ({
    identifier,
    reportId,
    type,
  }: {
    identifier: string;
    reportId: number;
    type: PinnedVisualType;
  }): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.post("/pinnedVisuals/unpinVisuals", [
        {
          identifier,
          reportId,
          type,
        },
      ])
        .then((res) => {
          if (res.data.success) resolve(true);
          else reject("Failed to unpin visual to dashboard");
        })
        .catch(reject);
    });
  };

  togglePinToDashboardChart = (
    chartId: number,
    value: boolean
  ): Promise<Chart> => {
    return new Promise((resolve, reject) => {
      API.post("/reports/pintodashboard/chart", {
        chartId,
        value,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.chart);
          } else {
            reject("Failed to pin chart to dashboard");
          }
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  togglePinToDashboardCard = (
    cardId: number,
    value: boolean
  ): Promise<Chart> => {
    return new Promise((resolve, reject) => {
      API.post("/reports/pintodashboard/card", {
        cardId,
        value,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.card);
          } else {
            reject("Failed to pin card to dashboard");
          }
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  togglePinToDashboardPivot = (
    pivotTableId: number,
    value: boolean
  ): Promise<Chart> => {
    return new Promise((resolve, reject) => {
      API.post("/reports/pintodashboard/pivot", {
        pivotTableId,
        value,
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.pivotTable);
          } else {
            reject("Failed to pin pivot to dashboard");
          }
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  toggleDashboardBatch = (
    chartIds: number[],
    pivotTableIds: number[],
    value: boolean
  ): Promise<{ charts: Chart[]; pivotTables: PivotTable[] }> => {
    return new Promise((resolve, reject) => {
      API.post("/reports/addtodashboard/batch", {
        chartIds,
        pivotTableIds,
        value,
      })
        .then((res) => {
          if (res.data.success) {
            resolve({
              charts: res.data.charts,
              pivotTables: res.data.pivotTables,
            });
          } else {
            reject("Failed to add to dashboard");
          }
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  togglePinToDashbaordBatch = (
    reportId: number,
    chartIds: number[],
    pivotTableIds: number[],
    cardIds: number[],
    cohortIds: number[],
    value: boolean
  ): Promise<{
    charts: Chart[];
    pivotTables: PivotTable[];
    cards: Cards[];
    cohorts: Cohort[];
  }> => {
    return new Promise((resolve, reject) => {
      API.post("/reports/pintodashboard/batch", {
        chartIds,
        pivotTableIds,
        cardIds,
        cohortIds,
        value,
        reportId,
      })
        .then((res) => {
          if (res.data.success) {
            resolve({
              charts: res.data.charts,
              pivotTables: res.data.pivotTables,
              cards: res.data.cards,
              cohorts: res.data.cohorts,
            });
          } else {
            reject("Failed to add to dashboard");
          }
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  shareReportEmail = (body): Promise<void> => {
    return new Promise((resolve, reject) => {
      API.post("/reports/shareReportEmail", body)
        .then((res) => {
          if (res.data.status) {
            resolve();
          } else {
            reject("Failed to send mail");
          }
        })
        .catch((error) => {
          //
          return reject(error.response.data);
        });
    });
  };

  getDashboardDatapieces = (
    category?: string
  ): Promise<DashboardDataPieces> => {
    return new Promise((resolve, reject) => {
      API.get("/reports/dashboard/datapieces", {
        params: {
          category,
        },
      })
        .then((res) => {
          if (res.data.success) {
            // res.data.pinnedCards = res.data?.pinnedCards?.map((item) => {
            //   item.config["formula"] =
            //     "New Customers who became Repeat Customers / Total Number of New Customers";
            //   return item;
            // });
            // res.data.cohorts =
            //   res.data?.cohorts?.map((item) => {
            //     if (item.title === "Net Sales Cohort")
            //       item.config["topHeader"] = [
            //         { col: item.config.colSpan, name: "" },
            //         { col: 13, name: "Repeat Orders" },
            //       ];
            //     return item;
            //   }) ?? [];
            resolve({
              pinnedCharts: res.data.pinnedCharts,
              charts: res.data.charts,
              pinnedPivotTables: res.data.pinnedPivotTables,
              pivotTables: res.data.pivotTables,
              pinnedCards: res.data.pinnedCards,
              pinnedCohorts: res.data.pinnedCohorts,
              reportObj: res.data?.reportObj,
            });
          } else {
            resolve({
              pinnedCharts: [],
              charts: [],
              pinnedPivotTables: [],
              pivotTables: [],
              pinnedCohorts: [],
              pinnedCards: [],
            });
          }
        })
        .catch((error) => {
          return reject(error.response.data);
        });
    });
  };

  getTrackerStatus = (trackerId: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      API.get("/tracker/status", {
        params: {
          trackerId,
        },
      })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data.status);
          } else {
            resolve(null);
          }
        })
        .catch((error) => {
          return reject(error.response.data);
        });
    });
  };

  reportExistByCompany = ({
    workspaceId,
    agencyClientId,
  }: {
    workspaceId: string;
    agencyClientId?: number;
  }): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.get("/reports/reportExistByCompany", {
        params: {
          workspaceId,
          agencyClientId,
        },
      })
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  draftReportByUser = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      API.get("/reports/getDraftReportByUser")
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  customdataperiodNew = (options: any, companyId = null): Promise<any> => {
    // console.log("customdataperiodNew before", options);
    return new Promise((resolve, reject) => {
      console.log("customdataperiodNew", options);
      const user = getLocalUser();
      const body = {
        ...options,
        companyId: companyId ? companyId : user.companyId,
        userId: user.id,
        checkExisting: false,
      };
      API.post("/charts/getLambdaCharts", body)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  customDataPeriodConsolidated = (body): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post("/reports/customdataperiod/consolidated/new", body)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  consolidatedLambdaCall = (body): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post("/Charts/consolidatedLambdaCall", body)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  insufficientWorkspace = (body): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post("/reports/customDataPeriod/insufficientWorkspace", body)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  cancelLambdaCall = (id): Promise<void> => {
    return new Promise((resolve, reject) => {
      API.get("/reports/customdataperiod/cancellambda?jobId=" + id)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  tempworkspaceClose = (id): Promise<void> => {
    return new Promise((resolve, reject) => {
      API.delete("/reports/tempworkspace?jobId=" + id)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  clientInvitingAgency = (user, reportIds) => {
    return new Promise((resolve, reject) => {
      API.post("/invites/agency", { user, reportIds })
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  agencyInvitingClient = (values) => {
    return new Promise((resolve, reject) => {
      API.post("/invites/client", values)
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  getAllCompaniesReportByReportName = (
    reportName: string,
    companyName: string
  ): Promise<Report[]> => {
    return new Promise((resolve, reject) => {
      API.get("/reports/companies", {
        params: {
          companyName,
          reportName,
        },
      })
        .then((res) => {
          resolve(res.data?.reports);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  deleteInvitedUser = (userId: number): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.delete("/invites/deleteinvite", {
        params: {
          userId,
        },
      })
        .then((res) => {
          resolve(true);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  getConnectedCompanies = (): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      API.get("/invites/connectedCompanies")
        .then((res) => resolve(res.data.companies))
        .catch((err) => reject(err));
    });
  };

  pullData = ({
    integrationPartner,
    extraParams = {},
  }: {
    integrationPartner: IIntegrationPartner;
    extraParams?: Record<string, any>;
  }): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      const user = getLocalUser();
      API.post("/charts/pullData", {
        integrationPartner,
        extraParams,
        companyId:
          user?.role == UserRole.Agency
            ? extraParams?.agencyClientId
            : user.companyId,
      })
        .then((res) => {
          resolve(res.data?.success);
        })
        .catch((error) => reject(error));
    });
  };

  getDFStatus = (
    companyId: number,
    workspaceMaxDataPeriod: Record<string, number> = null
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.get("/datafetching/getStatusByCompany", {
        params: { companyId, workspaceMaxDataPeriod },
      })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          console.log(err);
          resolve([]);
        });
    });
  };

  getDFStatusByIP = (
    integrationPartner,
    agCompanyId = null,
    workspaceMaxDataPeriod = null
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      const user = getLocalUser();
      const companyId = user?.role == "Agency" ? agCompanyId : user.companyId;
      API.get("/datafetching/getCDPCheckByIP", {
        params: { companyId, integrationPartner, workspaceMaxDataPeriod },
      })
        .then((res) => {
          resolve(res?.data);
        })
        .catch((err) => {
          console.log(err);
          resolve([]);
        });
    });
  };

  viewSelect = (
    reportId,
    accIds,
    integrationPartner,
    workspace,
    dataPeriod,
    dataPeriodArr,
    companyId = null,
    connectionId = null
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      const user = getLocalUser();
      const userCompanyId = user.companyId;
      const body = {
        reportId,
        accIds,
        companyId: companyId ? companyId : userCompanyId,
        userId: user.id,
        jobType: JobType.viewSelect,
        workspace,
        integrationPartner,
        checkExisting: true,
        dataPeriod,
        dataPeriodArr,
        connectionId,
      };
      API.post("/charts/getLambdaCharts", body)
        .then((res) => {
          // if (res?.data?.WORKSPACE_STATUS == WorkspaceStatus.exist) {
          resolve(res?.data);
          // } else {
          //   // SENT FOR CREATION
          //   resolve(0);
          // }
        })
        .catch((err) => {
          console.log(err);
          resolve([]);
        });
    });
  };

  getDataFetchingStatus = (): Promise<{
    status: number;
    type: LoadingProgressType;
    extraConfig: any;
  }> => {
    return new Promise((resolve, reject) => {
      API.get("/datafetching/status")
        .then((res) => resolve(res.data))
        .catch((error) => {
          reject(error?.response?.data);
        });
    });
  };

  updateConnectionIdByJobId = (
    jobId: number,
    connectionId: string
  ): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.put("/charts/updateConnectionIdByJobId", {
        body: { jobId, connectionId },
      })
        .then((res) => resolve(res.data.success))
        .catch((error) => reject(error));
    });
  };

  disconnectAPIGatewaySocket = (url: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.delete(url)
        .then((res) => resolve(res))
        .catch((error) => reject(error));
    });
  };

  getFilteredCohortData = (
    reportId: number,
    jobId: number,
    cohortId: number,
    firstFilter: string,
    otherFilter: string,
    isTemp: boolean = false
  ): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.get("/cohort/filter", {
        params: {
          reportId,
          jobId,
          cohortId,
          firstFilter,
          otherFilter,
          isTemp,
        },
      })
        .then((res) => resolve(res.data.data))
        .catch((error) => {
          reject(error);
        });
    });
  };

  getDecisionList = (companyId): Promise<DecisionWithLinking[]> => {
    return new Promise((resolve, reject) => {
      const query: string[] = [];
      if (companyId) query.push(`companyId=${companyId}`);
      API.get(`/decisionhub/decisionlist?${query.join("&")}`)
        .then((res) => resolve(res.data.data))
        .catch((error) => {
          reject(error);
        });
    });
  };

  doesRequiredAccountExists = (companyId: number): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      const query = [];
      if (companyId) query.push(`companyId=${companyId}`);
      API.get(`/goalsHub/checkRequiredChannel?${query.join("&")}`)
        .then((res) => resolve(res.data?.success))
        .catch((error) => {
          reject(error);
        });
    });
  };

  getCompaniesUnderAgency = (): Promise<Company[]> => {
    return new Promise((resolve, reject) => {
      API.get("/Companies/GetCompaniesByAgency")
        .then((res) => {
          resolve(res.data?.data?.companies);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  getTopReports = (companyId = null): Promise<TopReportsWithDatapiece[]> => {
    return new Promise((resolve, reject) => {
      API.get("/decisionhub/topreports", {
        params: {
          companyId: companyId,
        },
      })
        .then((res) => {
          if (res.status === 200) resolve(res.data?.data);
          else reject("Error: Get top reports failed");
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  getReportData = (
    selectedReportParams: GetReportDataParams
  ): Promise<ReportDataFromDH> => {
    return new Promise((resolve, reject) => {
      API.get("/goalsHub/report", {
        params: selectedReportParams,
      })
        .then((res) => {
          if (res.status === 200) resolve(res.data);
          else reject("Error: Get report data failed");
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  getInsights = (
    dpIdentifier: string,
    reportTitle: string,
    clientCompanyId?: number
  ): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      API.get("/decisionhub/insights", {
        params: {
          dpIdentifier: dpIdentifier,
          reportTitle: reportTitle,
          clientCompanyId,
        },
      })
        .then((res) => {
          if (res.status === 200) resolve(res.data?.insights);
          else reject("Error: Get report data failed");
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  updateVoteIdea = (
    insightId: number,
    vote: InsightVote,
    comments?: string
  ): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      API.post("/goalsHub/vote", {
        insightId,
        comments,
        vote,
      })
        .then((res) => {
          if (res.status === 200 || res.status === 201) resolve(res.data?.data);
          else reject("Error: Failed to send vote");
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  deleteVoteIdea = (insightId: number): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      API.delete(`/goalsHub/vote?insightId=${insightId}`)
        .then((res) => {
          if (res.status === 200) resolve(res.data?.voteData);
          else reject("Error: Failed to send vote");
        })
        .catch((error) => {
          reject(error);
        });
    });
  };
  getWorkspaceInsights = (workspaceName: string): any => {
    return new Promise((resolve, reject) => {
      API.get("/decisionhub/workspaceinsights", {
        params: {
          workspaceName,
        },
      })
        .then((res) => {
          if (res.status === 200) resolve(res.data?.insights);
          else reject("Error: Failed to get workspace insights");
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  regenerateInsights = (): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      API.post("/insights/regenerateInsights")
        .then((res) => {
          if (res.status === 200) resolve(true);
          else reject(false);
        })
        .catch((error) => {
          console.log(error);
          reject(false);
        });
    });
  };

  getGoalsAndIdeas = (): Promise<GoalsHubResponse> => {
    return new Promise((resolve, reject) => {
      API.get("/goalsHub/metricsList")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  getInsightFeed = (
    page: number,
    companyId: number,
    insightType?: InsightTypes,
    selectedInsightId?: number,
    dpIdentifier?: string,
    reportTitle?: string,
    workspaceName?: string
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      // console.log("workspace name", workspaceName);
      API.get("/insights/feed", {
        params: {
          page,
          companyId,
          insightType,
          selectedInsightId,
          dpIdentifier,
          reportTitle,
          workspaceName,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  updateLoginHistory = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post("/users/updateLoginHistory")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  getFinanceStatementTable = (
    showAllColumns: boolean = false,
    showOnlyPnlAdded: boolean = true
  ) => {
    return new Promise((resolve, reject) => {
      API.get("/finance/financestatement", {
        params: {
          showAllColumns: showAllColumns,
          showOnlyPnlAdded,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  getFinanceTabsData = () => {
    return new Promise((resolve, reject) => {
      API.get("/finance/tabs")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  insertInputMetricsData = (data) => {
    return new Promise((resolve, reject) => {
      API.post("/finance/addmetric", data)
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  getFinanceMetricsEditData = (metricId: number) => {
    return new Promise((resolve, reject) => {
      API.get("/finance/financemetricedit", {
        params: {
          metricId: metricId,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  getProductsData = (
    integrationPartner = null,
    searchTerm = null,
    pageNumber = 0,
    sortOrder = "ASC",
    isPageLimit = true,
    isMissingCosts = false,
    removeProductId = null,
    cachedProductMcFilter = null,
    isMissingTaxRate = false,
    cachedProductTrFilter = null,
    isProductSold = true
  ) => {
    return new Promise((resolve, reject) => {
      API.get("/products/getProductsWithCost", {
        params: {
          integrationPartner,
          searchTerm,
          pageNumber: pageNumber + 1,
          sortOrder,
          isMissingCosts,
          isPageLimit,
          removeProductId,
          cachedProductMcFilter,
          isMissingTaxRate,
          cachedProductTrFilter,
          isProductSold,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  copyProductCosts = (bodyData) => {
    return new Promise((resolve, reject) => {
      API.post("/products/copycosts", bodyData)
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  fetchProductEditData = (productId: number) => {
    return new Promise((resolve, reject) => {
      API.get("/products/fetchproduct", {
        params: {
          productId,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  editProductData = (bodyData) => {
    return new Promise((resolve, reject) => {
      API.post("/products/editproduct", bodyData)
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  editInputDataMetric = (bodyData) => {
    return new Promise((resolve, reject) => {
      API.post("/finance/editmetric", bodyData)
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  deleteMetric = (metricId: number) => {
    return new Promise((resolve, reject) => {
      API.delete("/finance/deletemetric", {
        params: {
          metricId,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  getExpiredAccountsCount = () => {
    return new Promise((resolve, reject) => {
      API.get("/accesstokens/expiredaccountscount")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  getAccountsByIpName = (ipName: string) => {
    return new Promise((resolve, reject) => {
      API.get("/accesstokens/fetchaccounts", {
        params: {
          ipName,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  deleteAccountFromFs = (bodyData) => {
    return new Promise((resolve, reject) => {
      API.post("/finance/deleteAccount", bodyData)
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  getDfStatusByIp = (ipName: string) => {
    return new Promise((resolve, reject) => {
      API.get("/datafetching/statusbyip", {
        params: {
          ipName,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  getAccessListByFeature = (featureType) => {
    return new Promise((resolve, reject) => {
      API.get("/featureaccess/accesstype", {
        params: {
          feature: featureType,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  updateAccessListByFeature = (bodyData) => {
    return new Promise((resolve, reject) => {
      API.post("/featureaccess/updateaccess", bodyData)
        .then((res) => {
          resolve(res.data);
        })
        .catch(reject);
    });
  };

  checkPcGetStarted = (): Promise<{
    success: boolean;
    allowGetStarted: boolean;
    coldState: PcColdStates;
    isFirstUser: boolean;
    redirectToHome: boolean;
    journeyData: any;
    allUserAccess: any[];
    showFs: boolean;
  }> => {
    return new Promise((resolve, reject) => {
      API.get("/finance/checkjourney")
        .then((res) => {
          resolve(res.data);
        })
        .catch(reject);
    });
  };

  updatePcJourney = (bodyData) => {
    return new Promise((resolve, reject) => {
      API.post("/finance/updatejourney", bodyData)
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  updateBulkFeatureAccess = (bodyData) => {
    return new Promise((resolve, reject) => {
      API.post("/featureaccess/updateaccessbulk", bodyData)
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  recalculateFsData = (
    companyId: number,
    generateMoves: boolean = true,
    isFirstTime: boolean = false
  ) => {
    return new Promise((resolve, reject) => {
      API.get("Finance/recalculateFSData", {
        params: {
          companyId,
          generateMoves,
          isFirstTime,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  checkJourneyRouteAccess = (): Promise<{
    isUpdateAvailable: boolean;
    isInProgress: boolean;
    isInActive: boolean;
    jobStatus: ProductFetchingProgress;
  }> => {
    return new Promise((resolve, reject) => {
      API.get("/finance/journeyrouteaccess", {
        params: {
          ipName: "Shopify",
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  fetchProductMissingCosts = (): Promise<{
    productCount: number;
    missingCostCount: number;
  }> => {
    return new Promise((resolve, reject) => {
      API.get("/products/missingcost")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  fetchProductCostUpdateStatus = (): Promise<{
    isJobPresent: boolean;
    isInProgress: boolean;
    status: ProductFetchingProgress;
    isProductUpdate: boolean;
  }> => {
    return new Promise((resolve, reject) => {
      API.get("/products/costupdatestatus")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  submitTaxInput = (taxData): Promise<TaxInputInf> => {
    return new Promise((resolve, reject) => {
      API.post("/finance/taxinput", taxData)
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  fetchTaxInput = (): Promise<{ taxInput: TaxInputInf }> => {
    return new Promise((resolve, reject) => {
      API.get("/finance/taxinput")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  fetchProductByid = (productId: number): Promise<{ product: any }> => {
    return new Promise((resolve, reject) => {
      API.get("/products/productById", {
        params: {
          productId,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  insertLoanDetails = (loanData) => {
    return new Promise((resolve, reject) => {
      API.post("/finance/addloan", loanData)
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  fetchLoanDetails = (fsMetricId: number): Promise<{ loanData: any }> => {
    return new Promise((resolve, reject) => {
      API.get("/finance/loandetails", {
        params: {
          fsMetricId,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  updateLoanDetails = (loanData): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post("/finance/editloan", loanData)
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  deleteLoanDetails = (fsMetricId: number): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.delete("/finance/loandetails", {
        params: {
          metricId: fsMetricId,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  getAccountsFetchingStatusForPc = (): Promise<{ loadingIps: string[] }> => {
    return new Promise((resolve, reject) => {
      API.get("/finance/dataloading/accounts")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  fetchAccessTokens = (): Promise<{ accounts: any[] }> => {
    return new Promise((resolve, reject) => {
      API.get("/accesstokens")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  fetchIntegrationSuggestions = (
    companyId: number
  ): Promise<{ success: boolean; suggestions: any[] }> => {
    return new Promise((resolve, reject) => {
      API.get(`/IntegrationSuggestions/getByCompany?companyId=${companyId}`)
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  fetchBrandCategoryOptions = (): Promise<{ categories: any[] }> => {
    return new Promise((resolve, reject) => {
      API.get("/brandcategories")
        .then((res) => resolve(res.data))
        .catch((err) => {
          console.log("Error in fetching brand categories", err);
        });
    });
  };

  fetchBrandKeyEvents = (): Promise<{ brandEvents: any[] }> => {
    return new Promise((resolve, reject) => {
      API.get("/brandevents")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  fetchKeyEventOptions = (): Promise<{ eventOptions: any[] }> => {
    return new Promise((resolve, reject) => {
      API.get("/events")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  fetchUnderwritingFormData = () => {
    return new Promise((resolve, reject) => {
      API.get("/underwriting")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  submitUnderwritingFormData = (formData: any) => {
    return new Promise((resolve, reject) => {
      API.post("/underwriting", formData)
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  submitBrandEvents = (formData: any) => {
    return new Promise((resolve, reject) => {
      API.post("/brandevents", formData)
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  checkInviteType = (type: LinkType) => {
    return new Promise((resolve, reject) => {
      API.get("/lendinglinks/checkinvite", {
        params: {
          type,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  checkUnderwritingJourney = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.get("/underwriting/checkjourney")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  checkMonthlyReportJourney = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.get("/monthlyreport/checkjourney")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  getDisbursementFormData = (
    formId: number
  ): Promise<{
    state: DisbursementColdState;
    success: boolean;
    disbursementData: any;
    formSteps: any[];
  }> => {
    return new Promise((resolve, reject) => {
      API.get("/disbursement", {
        params: {
          formId,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  getPrdCostUpdateAvailableStatus = (): Promise<{
    status: boolean;
  }> => {
    return new Promise((resolve, reject) => {
      API.get("/webhooks/prdCostUpdatesAvailable")
        .then((res) => resolve({ status: res.data.status }))
        .catch((err) => {
          console.log(err);
          resolve({ status: false });
        });
    });
  };

  updatePrdCost = () => {
    return new Promise((resolve, reject) => {
      API.get("/insights/updatePrdCost", {
        params: {
          fetchOnlyProductCosts: true,
          fetchOnlyFinance: true,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  submitDisbursementFormData = (formData: any) => {
    return new Promise((resolve, reject) => {
      API.post("/disbursement", formData)
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  prepareSheet = (bodyData: any) => {
    return new Promise((resolve, reject) => {
      API.post("/spreadsheet/products/preparesheet", bodyData)
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  checkExistingProductSheetTracker = (): Promise<{
    spreadsheetTracker: any;
  }> => {
    return new Promise((resolve, reject) => {
      API.get("/spreadsheet/product/checkexisting")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  refreshSpreadsheetData = () => {
    return new Promise((resolve, reject) => {
      API.get("/spreadsheet/product/updatecost")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  continueSheetUpdating = () => {
    return new Promise((resolve, reject) => {
      API.get("/spreadsheet/product/continueediting")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  isCODAvailable = () => {
    return new Promise((resolve, reject) => {
      API.get("/finance/isCODAvailable")
        .then((res) => resolve(res.data?.isCODAvailable || false))
        .catch(reject);
    });
  };

  getMissingCostItems = () => {
    return new Promise((resolve, reject) => {
      API.get("/finance/getMissingCostItems")
        .then((res) => {
          if (res?.data?.success) {
            resolve(res.data);
          } else {
            resolve(null);
          }
        })
        .catch(reject);
    });
  };

  getPinnedVisualsByType = (
    visualType: PinnedVisualType[]
  ): Promise<PinnedVisual[]> => {
    return new Promise((resolve, reject) => {
      API.get("/pinnedvisuals/type", {
        params: {
          type: visualType.toString(),
        },
      })
        .then((res) => {
          resolve(res.data?.data);
        })
        .catch(reject);
    });
  };

  updateOrderingCards = (
    cardOrder: PinnedVisual[],
    unpinCard: number[]
  ): Promise<PinnedVisual[]> => {
    return new Promise((resolve, reject) => {
      API.post("/pinnedvisuals/reorder", {
        cardOrder,
        unpinCard,
      })
        .then((res) => {
          resolve(res.data?.data);
        })
        .catch(reject);
    });
  };

  getDashboardMetricsList = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.get("/goalsHub/dashboard/metrics")
        .then((res) => resolve(res.data?.data))
        .catch(reject);
    });
  };

  getDashboardProductsTable = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.get("/goalshub/dashboard/pivotvalues")
        .then((res) => resolve(res.data?.data))
        .catch(reject);
    });
  };

  runDataPeriodDashboardJob = (bodyData) => {
    return new Promise((resolve, reject) => {
      API.post("/jobs/dashboard", bodyData)
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  fetchDashboardJobsData = (jobId) => {
    return new Promise((resolve, reject) => {
      API.get("/jobs/dashboard", {
        params: {
          jobId: jobId,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  fetchDashboardCdpStatus = () => {
    return new Promise((resolve, reject) => {
      API.get("/datafetching/dashboard/dataperiod/status")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  getExpiredAccounts = () => {
    return new Promise((resolve, reject) => {
      API.get("/AccessTokens/getExpiredAccounts")
        .then((res) => resolve(res?.data))
        .catch(reject);
    });
  };

  fetchFestivalsAndEvents = (startDate?: string, endDate?: string) => {
    return new Promise((resolve, reject) => {
      API.get("/events", {
        params: {
          startDate,
          endDate,
        },
      })
        .then((res) => resolve(res?.data))
        .catch(reject);
    });
  };

  submitWorkspaceComparisonJob = (body: any) => {
    return new Promise((resolve, reject) => {
      API.post("/jobs/workspace/comparison", body)
        .then((res) => resolve(res?.data))
        .catch(reject);
    });
  };

  fetchEarlyAccessIntegrations = () => {
    return new Promise((resolve, reject) => {
      API.get("/earlyaccessintegrations")
        .then((res) => resolve(res?.data))
        .catch(reject);
    });
  };

  fetchEarlyAccessIntegrationDetails = (agencyClientId: number = null) => {
    return new Promise((resolve, reject) => {
      API.get("/earlyaccessintegrations/accessdetails", {
        params: {
          agencyClientId,
        },
      })
        .then((res) => resolve(res?.data))
        .catch(reject);
    });
  };

  sendEarlyAccessIntegrationDetails = (bodyData) => {
    return new Promise((resolve, reject) => {
      API.post("/earlyaccessintegrations/accessdetails", bodyData)
        .then((res) => resolve(res?.data))
        .catch(reject);
    });
  };

  sendEarlyAccessOtpConfirmation = (
    accessDetailsId: number,
    agencyClientId: number
  ) => {
    return new Promise((resolve, reject) => {
      API.post("/earlyaccessintegrations/accessdetails/otpsetup", {
        accessDetailsId,
        agencyClientId,
      })
        .then((res) => resolve(res?.data))
        .catch(reject);
    });
  };

  sendUnderwritingUploadJob = (body: any) => {
    return new Promise((resolve, reject) => {
      API.post("/underwriting/uploadjob", body)
        .then((res) => resolve(res?.data))
        .catch(reject);
    });
  };

  /**
   * Every classification row for the company's form in one response:
   * `{ yearly: { [fyYear]: job }, last12Months: { requiredPeriod, job } }`.
   */
  fetchRevenueClassificationResult = () => {
    return new Promise((resolve, reject) => {
      API.get("/smartfin/classificationResult")
        .then((res) => resolve(res?.data))
        .catch(reject);
    });
  };

  fetchStatementAnalysisJob = (formType: ApplicationContextType) => {
    return new Promise((resolve, reject) => {
      API.get("/statementanalysis", {
        params: {
          formType,
        },
      })
        .then((res) => resolve(res?.data))
        .catch(reject);
    });
  };

  fetchGstAnalysisJob = (formType: ApplicationContextType) => {
    return new Promise((resolve, reject) => {
      API.get("/gstanalysis", {
        params: {
          formType,
        },
      })
        .then((res) => resolve(res?.data))
        .catch(reject);
    });
  };

  deleteUploadedJob = (body: any) => {
    return new Promise((resolve, reject) => {
      API.post("/underwriting/uploadjob/deletefile", body)
        .then((res) => resolve(res?.data))
        .catch(reject);
    });
  };

  fetchUploadedJobs = (
    formType: ApplicationContextType,
    monthYear: string = null
  ) => {
    return new Promise((resolve, reject) => {
      API.get("/underwriting/uploadjob")
        .then((res) => resolve(res?.data))
        .catch(reject);
    });
  };

  createNetBankingJob = (bodyData) => {
    return new Promise((resolve, reject) => {
      API.post("/statementanalysis/netbankingjob", bodyData)
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  overrideStatementAnalysisError = (jobId: number) => {
    return new Promise((resolve, reject) => {
      API.post("/statementanalysis/overrideerror", { jobId })
        .then((res) => resolve(res?.data))
        .catch(reject);
    });
  };

  revertStatementAnalysisError = (jobId: number) => {
    return new Promise((resolve, reject) => {
      API.post("/statementanalysis/reverterror", { jobId })
        .then((res) => resolve(res?.data))
        .catch(reject);
    });
  };

  checkIfEmailForwarded = (email: string) => {
    return new Promise((resolve, reject) => {
      API.get("/AccessTokens/checkemailforward", {
        params: {
          email,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  fetchDatalimitationAppConfig = () => {
    return new Promise((resolve, reject) => {
      API.get("/appconfig/datalimitation")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  fetchSIPromoConfig = () => {
    return new Promise((resolve, reject) => {
      API.get("/appconfig/si-promotions")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  generateGstOtp = (bodyData) => {
    return new Promise((resolve, reject) => {
      API.post("/scoreme/generateOTP", bodyData)
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  validateOtp = (bodyData) => {
    console.log("Validating OTP");
    return new Promise((resolve, reject) => {
      API.post("/scoreme/validateOTP", bodyData)
        .then((res) => resolve(res.data))
        .catch((err) => {
          console.log("Error in validating OTP", err);
        });
    });
  };

  fetchMonthlyReportFormData = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.get("/monthlyreport")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  submitMonthlyReportFormData = (formData: any) => {
    return new Promise((resolve, reject) => {
      API.post("/monthlyreport", formData)
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  getLendingRecipientList = () => {
    return new Promise((resolve, reject) => {
      API.get("/lendingRecipientList")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  sendSupportMessage = (
    message: string,
    service: string = "Monthly Report"
  ) => {
    return new Promise((resolve, reject) => {
      API.post("/monthlyreport/livesupport", { message, service })
        .then((res) => console.log(res))
        .catch(reject);
    });
  };

  fetchCompanyDataRestrictions = () => {
    return new Promise((resolve, reject) => {
      API.get("/appconfig/companydatarestrictions")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  fetchWorkspaceRestrictionsConfig = () => {
    return new Promise((resolve, reject) => {
      API.get("/appconfig/workspacerestrictions")
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  checkWorkspaceAgencyAccess = (
    workspaceName: string,
    agencyClientId: number
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.get("/accesstokens/checkworkspaceaccess/agency", {
        params: {
          workspaceName,
          agencyClientId,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };

  updateReportLinkRecipients = (
    reportIds: number,
    user: User
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      API.post(`/Reports/updateReportLinkRecipients`, { reportIds, user })
        .then((res) => {
          if (res.data.success) {
            resolve();
          } else reject(res.data.error);
        })
        .catch(reject);
    });
  };

  inviteBrand = (formDetails): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post(`/users/invitebrand`, formDetails)
        .then((res) => {
          resolve({
            success: true,
          });
        })
        .catch((err) => {
          console.log(err);
          resolve({
            success: false,
            err: err.response.data.errors,
          });
        });
    });
  };

  generateAndDownloadSFPLExcel = (values) => {
    return new Promise((resolve, reject) => {
      API.post("/underwriting/calculator/download", values, {
        responseType: "arraybuffer",
      })
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  getDashboardMetrics = (values): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post("/agencyDashboard", values)
        .then((res) => {
          console.log("res data", res.data);
          resolve(res.data);
        })
        .catch(reject);
    });
  };
  getDashboardMetricsWithoutBody = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      console.log("API CALLED");
      API.post("/agencyDashboard")
        .then((res) => {
          console.log("res data", res.data);
          resolve(res.data);
        })
        .catch(reject);
    });
  };

  getAgencyDashboardTarget = (
    metricId: number,
    agencyClientId: number
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.get("/agencyDashboard/target", {
        params: {
          metricId,
          agencyClientId,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };
  getTargetHistoryByClientId = (agencyClientId: number): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.get("/agencyDashboard/targetHistoryByClientId", {
        params: {
          agencyClientId,
        },
      })
        .then((res) => resolve(res.data))
        .catch(reject);
    });
  };
  getTargetHistoryForPrevMonth = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.get("/agencyDashboard/targetHistoryForPrevMonth")
        .then((res) => resolve(res))
        .catch(reject);
    });
  };
  // getTargetHistoryByClientId = (): Promise<any> => {
  //   return new Promise((resolve, reject) => {
  //     API.get("/agencyDashboard/targetHistoryForPrevMonth")
  //       .then((res) => resolve(res))
  //       .catch(reject);
  //   });
  // };

  setIsShownTargetHistory = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post("/agencyDashboard/target/setIsTargetHistoryShown", {
        is_shown: true,
      })
        .then((res) => resolve(res))
        .catch(reject);
    });
  };

  setAgencyDashboardTarget = (targetData: any[]): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post("/agencyDashboard/target", { targetData })
        .then((res) => {
          if (res.data.success) {
            resolve(res.data);
          } else {
            reject(res.data.error);
          }
        })
        .catch(reject);
    });
  };

  exportExcel = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post(
        "/underwriting/update-excel",
        {
          tenor: 12,
          loanSanctionAmount: 5000,
          annualRevenueRunRate: 120000,
        },
        { responseType: "blob" }
      )
        .then((res) => {
          // Create blob link
          const blob = new Blob([res.data], {
            type:
              res.headers["content-type"] ||
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          resolve(url);
        })
        .catch(reject);
    });
  };

  getAccessTokensByIp = (companyId: number, ipId: string): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      API.get(`/AccessTokens/getAccessTokens`, {
        params: { companyId, integrationPartnerId: ipId },
      })
        .then((res: any) => resolve(res.data?.accessTokens ?? []))
        .catch((err) => reject(err));
    });
  };

  zohoOnDemandRefresh = (companyId: number, accessTokenId: number): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post(`/Charts/zohoOnDemandRefresh`, { companyId, accessTokenId })
        .then((res: any) => resolve(res.data))
        .catch((err) => reject(err));
    });
  };

  // Goes through jigsaw-api (authenticated, via the API instance's own JWT
  // interceptor) rather than calling finance-api directly — finance-api's
  // /zoho/auth and /zoho/addCustomerDetails now only accept calls carrying
  // jigsaw-api's own system token, not arbitrary client-supplied companyIds.
  getZohoAuthUrl = (
    ipId: string,
    ipName: string,
    from: string,
    companyId?: number
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.get(`/gst/zoho/authUrl`, { params: { ipId, ipName, from, companyId } })
        .then((res: any) => resolve(res.data))
        .catch((err) => reject(err));
    });
  };

  confirmZohoConnection = (
    ipId: string,
    accDetails: Array<{ parentId: string; parentName: string }>,
    companyId?: number
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post(`/gst/zoho/confirm`, { ipId, accDetails, companyId })
        .then((res: any) => resolve(res.data))
        .catch((err) => reject(err));
    });
  };

  disconnectZoho = (accessTokenId: number, companyId?: number): Promise<any> => {
    return new Promise((resolve, reject) => {
      API.post(`/gst/zoho/disconnect`, { accessTokenId, companyId })
        .then((res: any) => resolve(res.data))
        .catch((err) => reject(err));
    });
  };

}

export let RestS = new RestService();
